import { ICategoryPageMatch } from "@faire/retailer-shared/categories/ICategoryPageMatch";
import { buildCategoryNavigationPath } from "@faire/retailer-shared/categories/navigationPath";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { Category } from "@faire/retailer-visitor-shared/models/Category/Category";
import { fromUrlIds } from "@faire/retailer-visitor-shared/models/Category/fromUrlIds";
import { useRetailerRoutingContext } from "@faire/retailer-visitor-shared/RetailerRoutingContextProvider";
import { useCategoryStore } from "@faire/retailer-visitor-shared/stores/domain/useCategoryStore";
import { getCategoryRedirect } from "@faire/retailer-visitor-shared/stores/domain/CategoryStore/getCategoryRedirect";
import { LoggedInCategoryPage } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/LoggedInCategoryPage";
import { useLoggedOutCategoryPage } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/LazyLoggedOutCategoryPage";
import { LoggedOutCategoryPageLoadingSkeleton } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/CategoryPageLoadingSkeleton/LoggedOutCategoryPageLoadingSkeleton";
import { LazySuppliersPage } from "@faire/retailer-visitor-shared/views/suppliers/LazySuppliersPage";
import { SuppliersPageLoadingSkeleton } from "@faire/retailer-visitor-shared/views/suppliers/SuppliersPageLoadingSkeleton/SuppliersPageLoadingSkeleton";
import { logAssert } from "@faire/web--source/common/logging";
import { isPrerender } from "@faire/web--source/common/user-agent/isPrerender";
import React, { Suspense, type JSX } from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";

export const CategoryPageRoot = ({
  match,
  location,
}: RouteComponentProps<ICategoryPageMatch> & {
  renderLoggedOutCategoryPage: (
    key: string,
    category: Category,
    navigationPath: string
  ) => JSX.Element;
}) => {
  const categoryUrlIds = extractCategoryUrlIds(match.params);
  const routingContext = useRetailerRoutingContext();
  const { categories } = useCategoryStore();
  const category = fromUrlIds(categoryUrlIds, categories ?? []);
  const LoggedOutCategoryPage = useLoggedOutCategoryPage();

  logAssert(
    !!category,
    `Category creation failed on url, Pathname: ${location.pathname}. Search: ${location.search}`
  );

  const categoryRedirectUrl = getCategoryRedirect(
    category,
    { match, location },
    categories ?? [],
    routingContext
  );
  if (categoryRedirectUrl !== undefined) {
    return <Redirect to={categoryRedirectUrl} />;
  }

  const { mostSpecificPathIdentifier } = category;
  const { brandValue, c1SuppliersUrlId } = match.params;
  const key = `${mostSpecificPathIdentifier}${brandValue}${c1SuppliersUrlId}`;
  const navigationPath = buildCategoryNavigationPath(categoryUrlIds);

  if (isPrerender() || !isLoggedInRetailerOrBrandPreview()) {
    if (!category.mostSpecificCategory.isProductFirstCategory) {
      return (
        <Suspense fallback={<SuppliersPageLoadingSkeleton />}>
          <LazySuppliersPage
            key={key}
            category={category}
            navigationPath={navigationPath}
          />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<LoggedOutCategoryPageLoadingSkeleton />}>
        <LoggedOutCategoryPage
          key={key}
          category={category}
          navigationPath={navigationPath}
        />
      </Suspense>
    );
  } else {
    return (
      <LoggedInCategoryPage
        key={key}
        category={category}
        navigationPath={navigationPath}
      />
    );
  }
};

const extractCategoryUrlIds = ({
  c1CategoryUrlId,
  c2CategoryUrlId,
  c3CategoryUrlId,
  c4CategoryUrlId,
  c5CategoryUrlId,
  c6CategoryUrlId,
  c1SuppliersUrlId,
}: ICategoryPageMatch): string[] => {
  return [
    c1CategoryUrlId ?? c1SuppliersUrlId ?? "",
    c2CategoryUrlId ?? "",
    c3CategoryUrlId ?? "",
    c4CategoryUrlId ?? "",
    c5CategoryUrlId ?? "",
    c6CategoryUrlId ?? "",
  ]
    .filter((urlId) => urlId !== "")
    .map(decodeURIComponent);
};
